import { flow, getEnv, Instance, types } from "mobx-state-tree";
import { enUS, de } from "date-fns/locale";

import { browser } from "../browser-info/BrowserService";
import { appLocalStorage } from "../storage/LocalStorageService";
import { registerGlobalService } from "../service-provider/ServiceRegistry";
import Language from "./language";
import { endpoints } from "../api/ApiEndpoints";
import { api } from "../api/ApiService";

const knownLanguages = Object.keys(Language);
const LanguageStorageKey = "language";
export interface TranslationContext {
  [key: string]: string | number;
}

export const TranslationServiceModel = types
  .model({
    language: types.optional(
      types.enumeration<Language>(Object.values(Language)),
      Language.english
    ),
    enTranslations: types.frozen(),
    deTranslations: types.frozen(),
  })
  .views((self) => ({
    get translations() {
      if (self.language === Language.english) return self.enTranslations;
      if (self.language === Language.german) return self.deTranslations;
      return {};
    },
    get translationsLoaded() {
      return !!self.enTranslations && !!self.deTranslations;
    },
  }))
  .actions((self) => {
    const getInitialLanguage = () => {
      let initialLanguage = getEnv(self).appLocalStorage.getItem(LanguageStorageKey) as Language;
      if (!initialLanguage) {
        const language = getEnv(self).browser.language as Language;
        initialLanguage = knownLanguages.indexOf(language) > -1 ? language : Language.english;
        getEnv(self).appLocalStorage.setItem(LanguageStorageKey, initialLanguage);
      }
      return initialLanguage;
    };
    const loadTranslations = flow(function* loadTranslations() {
      try {
        const endpoint = endpoints.get("translations");
        const response = yield getEnv(self).api.get(endpoint);
        self.enTranslations = response.englishTranslations;
        self.deTranslations = response.germanTranslations;
      } catch (error) {
        console.error(error);
      }
    });

    return {
      async afterCreate() {
        self.language = getInitialLanguage();
        await loadTranslations();
      },
      changeLanguage(language: Language) {
        if (language !== Language.keys)
          getEnv(self).appLocalStorage.setItem(LanguageStorageKey, language as string);
        self.language = language;
      },
    };
  })
  .views((self) => {
    const getTranslation = (translationKey: string) => {
      if (self.language === Language.keys) return translationKey;

      let enTranslation;
      if (!self.translations || !self.enTranslations) return translationKey;

      if (self.enTranslations[translationKey] || self.enTranslations[translationKey] === "") {
        enTranslation = `en-${self.enTranslations[translationKey]}`;
      }

      if (self.translations[translationKey] === "") return "";

      return self.translations[translationKey] || enTranslation || translationKey;
    };

    return {
      translate(translationKey: string, context?: TranslationContext) {
        if (!self.translationsLoaded) return "";
        const translation = getTranslation(translationKey);

        if (!context) return translation;

        let translationWithContext = translation;

        const keys = Object.keys(context);
        keys.forEach((key: string) => {
          const translatedValue =
            typeof context[key] === "string"
              ? getTranslation(context[key] as string)
              : context[key];

          translationWithContext = translationWithContext.replace(
            new RegExp(`%${key}%`, "g"),
            translatedValue
          );
        });

        return translationWithContext;
      },
      get locale() {
        return translationService.language === Language.german ? de : enUS;
      },
      get loadingText() {
        if (self.translationsLoaded) return `${getTranslation("general.loading")}...`;
        if (self.language === Language.english) return "loading...";
        if (self.language === Language.german) return "laden...";
        return "";
      },
    };
  });

export type TranslationService = Instance<typeof TranslationServiceModel>;
export const translationService = TranslationServiceModel.create(
  {},
  {
    api,
    browser,
    appLocalStorage,
  }
);
export default registerGlobalService(translationService as TranslationService);
